import { ExtendedArticleSpecialPriceImportDataFromFile } from '../../../../services/configuration.service';

// `IOptionsData['priceCategories']` is a loose `Record<string, any>[]` in the
// import tab, so the shape is stated here rather than imported.
export interface IPriceCategoryOption extends Record<string, any> {
  id?: string | number;
  label?: string | null;
}

export type ResolvedSpecialPriceRow = ExtendedArticleSpecialPriceImportDataFromFile;

/**
 * A special price sheet may name its price columns after the price category
 * ("Hauslieferung", "Livraison à domicile") instead of numbering them
 * ("Preis 3"). The backend refuses to guess a category for those — inferring it
 * from the column's position would put a sheet exporting categories 1, 3 and 7
 * onto 1, 2 and 3 — and hands them over as `unresolvedPrices` instead.
 *
 * They are matched HERE, against the tenant's own price-category dictionary,
 * because the dictionary lives on the client and the import lambda has no
 * database connection. Whatever still finds no category is collected in
 * `unmappedPriceHeaders` and shown in the preview, so the customer sees which
 * column was ignored rather than silently losing it.
 */
export const resolveSpecialPriceCategories = (
  value: ExtendedArticleSpecialPriceImportDataFromFile,
  priceCategories: IPriceCategoryOption[],
): ResolvedSpecialPriceRow => {
  const { unresolvedPrices, ...row } = value;
  const resolved: ResolvedSpecialPriceRow = {
    ...(row as ResolvedSpecialPriceRow),
    unmappedPriceHeaders: [],
  };

  for (const column of unresolvedPrices ?? []) {
    // The backend already knows this header states a price category another
    // column claimed first ("Preis 1" next to "Preis 1 neu"). Which of the two
    // the customer meant cannot be decided here, so it is never assigned - it is
    // reported, and the customer fixes the sheet.
    if (column.duplicateOfCategory != null) {
      resolved.unmappedPriceHeaders.push(column.header);
      continue;
    }

    const category = priceCategories.find(
      ({ label }) =>
        Boolean(label) &&
        normalizePriceCategoryName(String(label)) === normalizePriceCategoryName(column.header),
    );
    const field = category && (`price${category.id}` as keyof ResolvedSpecialPriceRow);

    // A category the sheet already filled by number wins over a name match, so a
    // file carrying both "Preis 3" and the name of category 3 cannot flip-flop
    // depending on column order.
    if (field && resolved[field] == null) {
      (resolved as Record<string, unknown>)[field] = column.value;
    } else {
      resolved.unmappedPriceHeaders.push(column.header);
    }
  }

  return resolved;
};

const normalizePriceCategoryName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
